import React, { useState, useCallback } from "react";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import LockIcon from "@styles/all-images/icons/lock.svg";
import UnlockIcon from "@styles/all-images/icons/unlock.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import SendIcon from "@/styles/all-images/icons/send.svg";
import CustomTable from "@/components/common/table/CustomTable";
import dateHandler from "@/composables/dateUtil";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import StaffManagementEditCard from "./StaffManagementEditCard";
import StaffAdd from "./StaffAdd";
import {
  ProfileApprovalStatus,
  ProfileApprovalStatusText,
  getStatusStyle,
} from "@/constants/enums/approval-status.enum";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { UserManagementStatus } from "@/constants/enums/user-management-status.enum";
import { usePermissionState } from "@/providers/PermissionProvider";
import { StaffManagementPermissions } from "@/constants/permission-modules/base-permissions";

interface StaffData {
  id: number;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  user_type_id: number;
  user_type: string;
  profile_picture: string | null;
  is_active: boolean;
  approval_status: number;
  accept_tc: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}
interface AssignedProfile {
  id: number;
  name: string;
}
const StaffManagement = () => {
  const { formatDate } = dateHandler();
  const dispatch = useDispatch<AppThunkDispatch>();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [forceReset, setForceReset] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isPermitted } = usePermissionState();

  const navigate = useNavigate();

  // Define columns for the CustomTable
  const columns = [
    {
      header: "User Name",
      accessor: (row: any) => `${row.first_name} ${row.last_name}`,
      sortable: true,
      sortKey: "first_name",
    },
    {
      header: "Email",
      accessor: "email",
      sortable: true,
      sortKey: "email",
    },
    {
      header: "Contact Number",
      accessor: "phone",
      sortable: true,
      sortKey: "phone",
    },
    {
      header: "Assigned Profile",
      accessor: (row: any) =>
        row.assigned_profiles?.length > 0 ? (
          <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-[100px] tw-max-w-[300px] tw-flex-wrap">
            {row.assigned_profiles
              .slice(0, 2)
              .map((profile: AssignedProfile) => (
                <span
                  key={profile.id}
                  className="tw-px-2 tw-bg-[#F8FAFC] tw-py-1 tw-border tw-border-[#E9EAEB] tw-rounded-full tw-text-sm tw-text-[#232323]"
                >
                  {profile.name}
                </span>
              ))}
            {row.assigned_profiles.length > 2 && (
              <span className="tw-px-2 tw-bg-[#146c94] tw-text-white custom-chip-bg tw-py-1 tw-border tw-border-[#E9EAEB] tw-rounded-full tw-text-sm tw-text-[#232323]">
                +{row.assigned_profiles.length - 2}
              </span>
            )}
            {isPermitted(
              StaffManagementPermissions.UPDATE_STAFF_MANAGEMENT,
            ) && (
              <button onClick={() => handleEditProfile(row)} title="Edit">
                <img
                  src={EditIcon}
                  alt="Edit Icon"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            )}
          </div>
        ) : (
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-px-2 tw-bg-[#F8FAFC] tw-py-1 tw-border tw-border-[#E9EAEB] tw-rounded-full tw-text-sm tw-text-[#232323]">
              No Profile Assigned
            </span>
          </div>
        ),
    },
    {
      header: "Status",
      accessor: (row: any) => {
        const status = row.status as ProfileApprovalStatus;
        const { bgColor, textColor } = getStatusStyle(status);

        return (
          <li
            className={`tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-text-xs tw-w-fit ${bgColor} ${textColor}`}
          >
            <span
              className={`tw-w-2 tw-h-2 tw-rounded-full tw-p-1 ${
                row.status === 1 ? "tw-bg-[#717680]" : "tw-bg-[#12B76A]"
              }`}
            ></span>
            {ProfileApprovalStatusText[status]}
          </li>
        );
      },
      sortKey: "approval_status",
    },
    {
      header: "Created On",
      accessor: (row: any) => formatDate(row.created_at),
      sortable: true,
      sortKey: "created_at",
    },
    {
      header: "Created By",
      accessor: "created_by",
      sortable: true,
      sortKey: "created_by",
    },
    ...(isPermitted(StaffManagementPermissions.UPDATE_STAFF_MANAGEMENT)
      ? [
          {
            header: "Action",
            accessor: (row: any) => {
              const handleBlockStatus = async (staff: StaffData) => {
                try {
                  const { authenticatedCall } = actionsHandler(
                    "updateBlockedStatus",
                  );
                  const response = await dispatch(
                    authenticatedCall({
                      method: "post",
                      payload: {
                        id: staff.id,
                        status: !staff.is_active,
                      },
                    }),
                  );

                  if (response.ok) {
                    toast.success(response.data?.message);
                    setForceRefresh(true);
                  }
                } catch (error) {
                  console.error("Error updating block status:", error);
                }
              };

              const handleSend = async (row: any) => {
                try {
                  setIsLoading(true);
                  const { authenticatedCall } =
                    actionsHandler("invitationResend");

                  const response = await dispatch(
                    authenticatedCall({
                      payload: {
                        email: row.email,
                      },
                    }),
                  );

                  if (response.ok) {
                    toast.success(
                      response.data?.message || "Invitation sent successfully",
                    );
                    setForceRefresh(true);
                  }
                } catch (error) {
                  console.error("Error sending invitation:", error);
                } finally {
                  setIsLoading(false);
                }
              };

              return (
                <div className="tw-flex tw-items-center">
                  <button
                    onClick={() => handleBlockStatus(row)}
                    title={row.is_active ? "Block User" : "Unblock User"}
                  >
                    <img
                      src={row.is_active ? UnlockIcon : LockIcon}
                      alt={row.is_active ? "Unblock User" : "Block User"}
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                  {row.status === UserManagementStatus.OP && (
                    <>
                      <button onClick={() => handleSend(row)} title="Resend">
                        <img
                          src={SendIcon}
                          alt="Resend"
                          className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                        />
                      </button>
                    </>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const handleFetch = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      try {
        const { getAllData } = actionsHandler("staffList");

        // Build ordering string
        let ordering = "";
        if (params.sortColumn) {
          ordering =
            params.sortDirection === "desc"
              ? `-${params.sortColumn}`
              : params.sortColumn;
        }

        const response = await dispatch(
          getAllData({
            limit: params.limit || 10,
            page: params.page || 1,
            search: params.search || "",
            customQuery: ordering ? `ordering=${ordering}` : "",
          }),
        );
        return response;
      } catch (error) {
        console.error("Error fetching staff data:", error);
        return {};
      }
    },
    [dispatch],
  );

  const handleEditProfile = (staff: any) => {
    setSelectedStaff(staff);
    setIsModalOpen(true);
  };

  const handleAddStaff = () => {
    setIsStaffModalOpen(true);
  };

  const handleCloseStaffModal = () => {
    setIsStaffModalOpen(false);
    setForceRefresh(true);
  };

  const handleCloseEditModal = (type: "close" | "submit") => {
    setIsModalOpen(false);
    if (type === "submit") {
      setForceRefresh(true);
    }
  };

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <img
                src={BackIcon}
                alt="Back Icon"
                onClick={() => navigate("/menu")}
                className="tw-cursor-pointer"
              />
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Operations Team
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tw-mt-[147px]">
        <CustomTable
          columns={columns}
          fetchData={handleFetch}
          addButton={{
            text: "Add Member",
            icon: PlusIcon,
            onClick: handleAddStaff,
          }}
          forceRefresh={forceRefresh}
          setForceRefresh={setForceRefresh}
          forceReset={forceReset}
          setForceReset={setForceReset}
          noDataMessage={`No staff found`}
          noSearchDataMessage={`No matching staff found`}
        />

        {/* Modals */}
        {isModalOpen && (
          <StaffManagementEditCard
            onClose={() => handleCloseEditModal("close")}
            onSubmit={() => handleCloseEditModal("submit")}
            staffId={selectedStaff.id}
            selectedProfiles={
              selectedStaff.assigned_profiles?.map(
                (profile: any) => profile.id,
              ) || []
            }
          />
        )}

        <div
          className={`tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-[310px] tw-bg-white tw-shadow-lg tw-transition-transform tw-duration-300 tw-ease-in-out ${
            isStaffModalOpen
              ? "tw-transform tw-translate-x-0"
              : "tw-transform tw-translate-x-full"
          } tw-z-50`}
        >
          {isStaffModalOpen && (
            <StaffAdd
              onClose={handleCloseStaffModal}
              onSubmit={handleCloseStaffModal}
              isStaffModalOpen={isStaffModalOpen}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffManagement;
